import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';

import type { Avatar, Maybe, Menu, MenuItemType } from '@evc/web-components';
import {
  breakpoints,
  BtnActionEnum,
  ButtonComponent,
  icons,
  ResponsiveService,
} from '@evc/web-components';

import { AuthService } from '../../clients/core/auth/service/auth.service';
import type { Organization } from '../../clients/core/organizations/organizations.type';
import { UserService } from '../../clients/core/user/user.service';
import { PlatformConfigService } from '../config/config.service';
import { I18nPlatformService } from '../i18n/i18n.service';
import { LanguageService } from '../i18n/language.service';
import { Guid } from '../types/api-objects.type';
import { AppIdentity } from '../types/app-identity.type';
import { LogoComponent } from './logo/logo';
import { AppsMenuComponent } from './menus/apps/apps-menu';
import { NavigationsService } from './navigations.service';
import { NavigationPrimaryComponent } from './primary/navigation-primary';
import { NavigationSecondaryComponent } from './secondary/navigation-secondary';
import { NavigationTertiaryComponent } from './tertiary/navigation-tertiary';
import {
  NAVIGATIONS_HOSTS,
  NAVIGATIONS_MODES,
  NavigationsMode,
  SecondaryNavigationHosts,
  TertiaryNavigationHosts,
} from './types';

@Component({
  selector: 'evc-navigations',
  imports: [
    AppsMenuComponent,
    LogoComponent,
    NavigationPrimaryComponent,
    NavigationSecondaryComponent,
    NavigationTertiaryComponent,
    ButtonComponent,
  ],
  templateUrl: './navigations.html',
  styleUrls: ['./navigations.scss', './navigations-ims.scss'],
  host: {
    '[attr.data-leftbar-open]': 'isLeftbarOpen()',
    '[attr.data-burger-open]': 'isBurgerMenuOpen()',
    '[attr.data-theme]': "isIms() ? 'ims' : null",
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationsComponent {
  readonly useLeftbar = input(false);
  readonly isIms = input(false);
  readonly displayThemeSelector = input(true);
  readonly displayUserMenu = input(true);
  readonly inputForcedLayout = input<Maybe<NavigationsMode>>(undefined);
  readonly navigationSecondary = input<Menu[]>([]);

  readonly login = input<Maybe<() => void>>(undefined);
  readonly logout = input<Maybe<() => void>>(undefined);
  readonly changeOrganization = input<Maybe<(organizationId: Guid) => void>>(undefined);

  readonly #userService = inject(UserService);
  readonly #authService = inject(AuthService);
  readonly #platformConfigService = inject(PlatformConfigService);
  readonly #i18nPlatformService = inject(I18nPlatformService);
  readonly #languageService = inject(LanguageService);
  readonly #navigationsService = inject(NavigationsService);
  readonly #responsiveService = inject(ResponsiveService);

  protected readonly icons = icons;

  protected readonly isLeftbarOpen = this.#navigationsService.isLeftbarOpen.bind(
    this.#navigationsService,
  );
  protected readonly toggleLeftbar = this.#navigationsService.toggleLeftbar.bind(
    this.#navigationsService,
  );

  protected readonly isBurgerMenuOpen = this.#navigationsService.isBurgerMenuOpen.bind(
    this.#navigationsService,
  );
  protected readonly toggleBurgerMenu = this.#navigationsService.toggleBurgerMenu.bind(
    this.#navigationsService,
  );

  readonly #wasAboveThreshold = signal(window.innerWidth >= breakpoints.desktop);
  readonly #windowWidth = signal(window.innerWidth);

  protected readonly isWindowBelowThreshold = computed<boolean>(() => {
    return this.#windowWidth() < breakpoints.desktop;
  });

  constructor() {
    window.addEventListener('resize', () => {
      this.#windowWidth.set(window.innerWidth);
    });

    effect(() => {
      const isBelowThreshold = this.isWindowBelowThreshold();
      const wasAboveThreshold = this.#wasAboveThreshold();

      if (isBelowThreshold && wasAboveThreshold) {
        this.toggleLeftbar(false);
      } else if (!isBelowThreshold && !wasAboveThreshold) {
        this.toggleLeftbar(true);
      }

      this.#wasAboveThreshold.set(!isBelowThreshold);
    });
  }

  protected readonly mode = computed<NavigationsMode>(() => {
    const forcedLayout = this.inputForcedLayout();
    if (forcedLayout) return forcedLayout;

    if (!this.#platformConfigService.useConnect()) return NAVIGATIONS_MODES.LEGACY;

    return this.#authService.isAuthenticated()
      ? NAVIGATIONS_MODES.PRIVATE
      : NAVIGATIONS_MODES.PUBLIC;
  });

  protected readonly secondaryHost = computed<SecondaryNavigationHosts>(() =>
    this.#responsiveService.isMobile() ? NAVIGATIONS_HOSTS.BURGER : NAVIGATIONS_HOSTS.LEFTBAR,
  );

  protected readonly tertiaryHost = computed<TertiaryNavigationHosts>(() =>
    this.#responsiveService.isMobile() ? NAVIGATIONS_HOSTS.BURGER : NAVIGATIONS_HOSTS.TOPBAR,
  );

  protected readonly shouldDisplayApplicationsIcon = computed<boolean>(() => {
    if (!this.#platformConfigService.useConnect()) return false;
    if (this.mode() === NAVIGATIONS_MODES.PUBLIC) return false;

    return !this.#platformConfigService.isStandaloneApplication();
  });

  currentOrganization: Organization | undefined;
  userAvatar = computed<Maybe<Avatar>>(() => this.#userService.profile()?.avatar);
  protected readonly appIdentity = this.#platformConfigService.getComputed<AppIdentity>('app');

  protected readonly navigationPrimary = this.#platformConfigService.getComputed<MenuItemType[]>(
    'topTabs',
    [],
  );
  protected readonly navigationTertiary = computed(() => ({
    settings: this.settings(),
    displayThemeSelector: this.displayThemeSelector(),
    help: this.help(),
    langs: this.langs(),
    user: this.displayUserMenu(),
    mode: this.mode(),
    host: this.tertiaryHost(),
  }));

  protected readonly settings = computed<Menu>(() =>
    this.#platformConfigService.getMenu('settings')(),
  );
  protected readonly help = this.#platformConfigService.getMenu('help');
  protected readonly langs = computed<Menu>(() => {
    const { languages } = this.#platformConfigService.get('i18n');
    const currentLanguage = this.#languageService.lang();

    return {
      title: this.#i18nPlatformService.t$('menus.language.title'),
      items: languages.map((lang) => ({
        text: this.#i18nPlatformService.t$(`menus.language.${lang}`),
        key: lang,
        selected: currentLanguage === lang,
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => {
            if (currentLanguage === lang) return;
            this.#languageService.setLanguage(lang);
          },
        },
      })),
    };
  });
}
